export const METALS = [
  { id: 'silver', name: 'Silver', label: 'Sterling Silver' },
  { id: 'white-gold', name: '14k White Gold', label: '14k White Gold' },
];

export const STONES = [
  { id: 'sky-blue', name: 'Sky Blue', label: 'Sky Blue Moissanite' },
  { id: 'colorless', name: 'Colorless', label: 'Colorless Moissanite' },
];

export const configInterest = (piece, metalId, stoneId) => {
  const metal = METALS.find((m) => m.id === metalId) || METALS[0];
  const stone = STONES.find((s) => s.id === stoneId) || STONES[0];
  return `${piece.name} — ${metal.label}, ${stone.label}`;
};

const piece = (slug, number, name, type, rest) => ({
  slug,
  number,
  name,
  type,
  image: `/products/${slug}.webp`,
  imageEx: `/products/${slug}-ex.webp`,
  cut: `/products/cut/${slug}.webp`,
  cutEx: `/products/cut/${slug}-ex.webp`,
  dimensions: 'Dimensions on request',
  ...rest,
});

export const DEEP_PIECES = [
  piece('the-sovereign', '01', 'The Sovereign', 'Pendant', {
    creed: 'Rule is not taken. It is received.',
    passage: ['An emblem of truer authority.', 'The Sovereign is granted, not seized.', 'It is the calm at the center of the current—', 'the one who does not chase the throne,', 'yet the throne aligns to them.'],
    symbolism: [
      { icon: 'marquise', text: 'The central marquise represents sovereign authority—wholeness, vision, and divine will.' },
      { icon: 'wave', text: 'The surrounding forms are the currents of the deep—loyal, eternal, and unwavering.' },
      { icon: 'ascend', text: 'The upward flow signifies ascension without leaving one’s depths.' },
    ],
    closing: ['The ocean does not', 'bow to the loud.', 'It bows to the deep.'],
    dimensions: '58mm × 30mm (excluding bail)',
    crops: { detail: { fx: 0.5, fy: 0.5, z: 2.6 }, upper: { fx: 0.5, fy: 0.18, ay: 0.3, z: 1.7 }, lower: { fx: 0.5, fy: 0.9, ay: 0.68, z: 1.7 } },
    float: 'tall', ratio: 0.464,
  }),
  piece('the-seal', '02', 'The Seal', 'Ring', {
    creed: 'Bound by the ocean. Marked by its will.',
    passage: ['A signature ring of authority and belonging.', 'The dominant marquise commands the flow of the design—', 'a mark of return. Micro marquise pavé runs in seamless', 'currents across the shoulders, carried by wave-like architecture.', 'Beneath the stone, a hidden trident: the seal of the house.'],
    symbolism: [
      { icon: 'ring', text: 'The closed band is the oath—unbroken, returned to, renewed.' },
      { icon: 'marquise', text: 'The dominant marquise is the mark of return: the one who went to the depths and came back owning them.' },
      { icon: 'trident', text: 'The hidden trident beneath the stone is the seal—known only to the wearer and the house.' },
    ],
    closing: ['What the ocean marks,', 'it does not forget.'],
    crops: { detail: { fx: 0.5, fy: 0.44, z: 2.4 }, upper: { fx: 0.5, fy: 0.3, ay: 0.4, z: 1.5 }, lower: { fx: 0.5, fy: 0.66, ay: 0.55, z: 1.5 } },
    float: 'tall', ratio: 1.284,
  }),
  piece('the-current', '03', 'The Current', 'Tennis Necklace', {
    creed: 'An endless flow of divine presence.',
    passage: ['A continuous current of sky-blue marquise light,', 'cut to flow with the grace of water and the strength of inheritance.', 'Fluidly articulated for movement and comfort; closed with a custom', 'clasp engraved with the IKARUS LANDED hallmark—made to endure.'],
    symbolism: [
      { icon: 'wave', text: 'Each marquise is a wave in the same current—no stone leads, none follows.' },
      { icon: 'infinity', text: 'The unbroken circle is presence without pause: the god who is everywhere the water is.' },
      { icon: 'link', text: 'The hallmark clasp is the vow that holds the flow—strength that is felt, not seen.' },
    ],
    closing: ['The current does not', 'ask for permission.', 'It arrives.'],
    crops: { detail: { fx: 0.5, fy: 0.78, z: 2.6 }, upper: { fx: 0.5, fy: 0.2, ay: 0.4, z: 2.0 }, lower: { fx: 0.72, fy: 0.62, z: 1.8 } },
    float: 'wide', ratio: 1.372,
  }),
  piece('the-tide', '04', 'The Tide', 'Tennis Bracelet', {
    creed: 'Waves that never lessen. Power that never fades.',
    passage: ['The Tide carries the rhythm of returning water—', 'constant, controlled, and inevitable.', 'Worn at the wrist, it moves with the body while holding', 'its own quiet authority: force in rhythm, never in haste.'],
    symbolism: [
      { icon: 'tide', text: 'The repeating marquise is the rhythm of the tide—arriving, withdrawing, arriving again.' },
      { icon: 'infinity', text: 'Flow without end: the line that circles the wrist as the sea circles the earth.' },
      { icon: 'anchor', text: 'Controlled force—the strength that holds its course beneath the surface.' },
    ],
    closing: ['The tide is never late.', 'It is never early.', 'It simply returns.'],
    crops: { detail: { fx: 0.5, fy: 0.74, z: 2.6 }, upper: { fx: 0.5, fy: 0.29, ay: 0.45, z: 2.0 }, lower: { fx: 0.75, fy: 0.6, z: 1.8 } },
    float: 'wide', ratio: 1.796,
  }),
  piece('the-meridian', '05', 'The Meridian', 'Necklace', {
    creed: 'A line of orientation, balance, and return.',
    passage: ['The Meridian marks the crossing between worlds—', 'where descent meets return, where intention becomes form.', 'A line of marquise light drawn around the throat; at its lowest', 'point a single stone turns downward, pointing the way back to the deep.'],
    symbolism: [
      { icon: 'meridian', text: 'The meridian is the line that divides and orients—one side the surface, the other the deep.' },
      { icon: 'bearing', text: 'The downward stone is a bearing: the path home is found by looking down, not up.' },
      { icon: 'infinity', text: 'Balance held in a circle—every departure already contains its return.' },
    ],
    closing: ['What leaves the deep', 'returns to the deep.'],
    crops: { detail: { fx: 0.5, fy: 0.84, ay: 0.55, z: 2.6 }, upper: { fx: 0.5, fy: 0.16, ay: 0.4, z: 2.0 }, lower: { fx: 0.25, fy: 0.6, z: 1.8 } },
    float: 'wide', ratio: 1.279,
  }),
  piece('the-drift', '06', 'The Drift', 'Band', {
    creed: 'A band of quiet movement and daily power.',
    passage: ['The smallest wearable expression of the current—', 'a refined line of sky-blue marquise light shaped for daily wear.', 'Marquise only. Minimal metal. Maximum flow.', 'Made to be stacked, layered, and lived in.'],
    symbolism: [
      { icon: 'wave', text: 'A single line of marquise is the current in miniature—constant, unhurried, alive.' },
      { icon: 'ring', text: 'The band is the daily oath—power worn without announcement.' },
      { icon: 'ascend', text: 'Stacked, it becomes a tide of its own: depth accumulated one line at a time.' },
    ],
    closing: ['The deep is not loud.', 'It is constant.'],
    crops: { detail: { fx: 0.5, fy: 0.62, z: 2.6 }, upper: { fx: 0.5, fy: 0.42, ay: 0.45, z: 1.9 }, lower: { fx: 0.72, fy: 0.6, z: 1.9 } },
    float: 'wide', ratio: 2.701,
  }),
  piece('baton-abissos', '07', 'Bâton Abissos', 'Masterwork', {
    creed: 'A trident-inspired weapon recovered as heirloom.',
    passage: ['A solid trident form—sculpted in white gold, cast as one,', 'and set edge to edge in micro pavé moissanite.', 'No visible metal. Only light.', 'A divine weapon reimagined as heirloom: a symbol of sovereignty', 'and command, forged for those who lead, carried for those who endure.'],
    symbolism: [
      { icon: 'trident', text: 'The trident is the oldest mark of dominion over the deep—command that was received, not seized.' },
      { icon: 'light', text: 'Full pavé without visible metal: power that is entirely light.' },
      { icon: 'anchor', text: 'Cast as one piece—unbroken lineage, an heirloom with no seam.' },
    ],
    closing: ['The sea has one sceptre.', 'It was never meant', 'to be held lightly.'],
    crops: { detail: { fx: 0.5, fy: 0.14, z: 2.6 }, upper: { fx: 0.5, fy: 0.3, z: 1.8 }, lower: { fx: 0.5, fy: 0.8, z: 1.8 } },
    float: 'tall', ratio: 0.257,
  }),
];

export const pieceIndex = (slug) => DEEP_PIECES.findIndex((p) => p.slug === slug);
